import { useCallback, useMemo } from 'react';
import { IObject } from '@utils/defaultTypes';
import { useSearchParams as useSearchParamsRRD } from 'react-router-dom';
import cleanObject from '@utils/cleanObject';
import deleteAttrInObj from '@utils/deleteAttrInObj';

interface IConfig {
  type?: 'pagination';
  defaultParams?: string[];
  extraParams?: string[]; // parameters currently used in table filters
}

const types = {
  pagination: ['page', 'linesPerPage'],
};

const DEFAULT_PAGINATION = {
  page: 1,
  linesPerPage: 20,
};

const useSearchParams = (config: IConfig) => {
  const [params, setParams] = useSearchParamsRRD();

  const allParamsNames = useMemo(() => {
    const paramsNames = config?.defaultParams ? [...config?.defaultParams] : [];
    if (config?.extraParams) {
      paramsNames.push(...config?.extraParams);
    }
    if (config?.type === 'pagination') {
      paramsNames.push(...types[config?.type]);
    }
    return paramsNames;
  }, [config?.defaultParams, config?.extraParams, config?.type]);

  const searchParams = useMemo(() => {
    const data: IObject = {};
    allParamsNames?.forEach(paramName => {
      data[paramName] = params.get(paramName);
    });
    return cleanObject(data);
  }, [allParamsNames, params]);

  const isResetPagination = useCallback((values: IObject) => {
    const objKeys = Object.keys(values);
    const hasPage = objKeys.includes('page');
    return !hasPage;
  }, []);

  const setSearchParams = useCallback(
    (values: IObject) => {
      let newValues = { ...searchParams, ...values };
      if (config?.type === 'pagination' && isResetPagination(values)) {
        newValues = {
          ...newValues,
          ...DEFAULT_PAGINATION,
        };
      }
      setParams(cleanObject(newValues), { replace: true });
    },
    [searchParams, config?.type, isResetPagination, setParams],
  );

  const hasValuesInExtraParams = useMemo(() => {
    return Object.keys(searchParams).some(paramName =>
      config?.extraParams?.includes(paramName),
    );
  }, [config?.extraParams, searchParams]);

  // by default 'extraParams' are deleted
  const clearSearchParams = useCallback(
    (paramsToDelete?: string[]) => {
      let newParams;
      const newExtraParams = config?.extraParams
        ? [...config?.extraParams]
        : [];
      const newParamsToDelete = paramsToDelete ?? newExtraParams;
      if (config?.type === 'pagination') {
        newExtraParams.push(...types[config?.type]);
      }
      if (newParamsToDelete) {
        newParams = deleteAttrInObj(searchParams, newParamsToDelete);
      }
      setParams(newParams, { replace: true });
    },
    [config?.extraParams, config?.type, setParams, searchParams],
  );

  return {
    searchParams,
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
  };
};

export default useSearchParams;
